import {useCallback, useMemo, useRef} from 'react';
import type {Dispatch, SetStateAction} from 'react';

import {modalFactory} from '../layout/Modal/Modal';

/**
 * The `useModal` hook provides an easy way to manage the opening and closing of a modal component.
 * It uses internal state to track whether the modal is open or closed, and returns a `Modal` component that can be rendered in the UI.
 * The hook provides `openModal` and `closeModal` functions for controlling the modal's visibility, and can also handle an optional `onClose` callback when the modal is closed.
 *
 * @returns An object containing the `Modal` component and functions to open and close it:
 * - `openModal`: Opens the modal by setting its state to `true`.
 * - `closeModal`: Closes the modal and optionally invokes a callback function.
 * - `Modal`: A memoized modal component created by `modalFactory`.
 *
 * @example
 * ```tsx
 * const { Modal, openModal, closeModal } = useModal();
 *
 * return (
 *   <>
 *     <button onClick={openModal}>Open Modal</button>
 *     <Modal>
 *       <p>Modal content here</p>
 *       <button onClick={() => closeModal(() => console.log('Modal closed'))}>Close Modal</button>
 *     </Modal>
 *   </>
 * );
 * ```
 */
export const useModal = () => {
    const state = useRef<Dispatch<SetStateAction<boolean>>>(null);
    const Modal = useMemo(() => modalFactory(state), []);

    /**
     * Opens the modal.
     * This function sets the state of the modal to `true`, which will trigger the modal to open.
     * It checks if the modal state reference (`state.current`) is available before attempting to change its value.
     * When called, this function is typically tied to a user action, such as clicking a button, to display the modal.
     */
    const openModal = useCallback(() => {
        if (state.current) {
            state.current(true);
        }
    }, [state]);

    /**
     * Closes the modal.
     * This function sets the state of the modal to `false`, effectively closing the modal.
     * It accepts an optional callback function `onClose`, which is executed after the modal is closed.
     * This is useful for executing additional logic once the modal is dismissed, such as cleaning up state or triggering events.
     *
     * @param onClose An optional callback function that gets executed after the modal is closed.
     */
    const closeModal = useCallback((onClose?: () => void) => {
        if (state.current) {
            if (typeof onClose === 'function') {
                onClose();
            }

            state.current(false);
        }
    }, [state]);

    return {
        closeModal,
        Modal,
        openModal
    };
};